import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { RefreshCw, Scale } from 'lucide-react';
import { fetchJson } from '../lib/api';

// Lo que quedo a medias con plata y espera a una persona (backend:
// services/conciliacion.py, revision del 26-oct). Antes estas marcas se
// escribian y ninguna pantalla las leia.
const SECCIONES = [
  { kind: 'label_purchase', title: 'Label purchases cut off midway',
    help: 'Charged, and the process stopped before the label was recorded. Check the carrier and the wallet.' },
  { kind: 'rate_option', title: 'Purchases waiting for the carrier',
    help: 'The carrier did not confirm. The charge is on hold until someone checks.' },
  { kind: 'wallet_alert', title: 'Wallet', help: 'Negative balances and movements finished or closed by the reconciler.' },
  { kind: 'merchant_payment_alert', title: 'SelleCheck', help: 'Captures, disputes and guarantee payouts that failed.' },
  { kind: 'credit_alert', title: 'SmartLGSTX credit', help: 'Over limit, voided after invoice, failed invoices.' },
];

const CAMPOS_OCULTOS = new Set(['resolved', 'booking', 'breakdown']);

function Resumen({ item }) {
  const pares = Object.entries(item).filter(([k, v]) => !CAMPOS_OCULTOS.has(k) && v !== null && v !== '' && typeof v !== 'object');
  const detalle = item.detail && typeof item.detail === 'object' ? Object.entries(item.detail) : [];
  return (
    <div className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
      {[...pares, ...detalle].map(([k, v]) => (
        <div key={k}><span className="text-slate-400">{k}:</span> <span className="font-mono">{String(v)}</span></div>
      ))}
    </div>
  );
}

function Fila({ kind, item, onResolved, token }) {
  const [nota, setNota] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const id = kind === 'rate_option' ? item.rate_id : item.id;

  const resolver = async () => {
    setEnviando(true);
    setError(null);
    try {
      await fetchJson(`/admin/reconciliation/${kind}/${encodeURIComponent(id)}/resolve`,
        { token, method: 'POST', body: { note: nota } });
      onResolved();
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 py-3 grid md:grid-cols-[1fr_320px] gap-3" data-testid={`recon-item-${kind}-${id}`}>
      <Resumen item={item} />
      <div className="space-y-2">
        <label className="block text-xs text-slate-500" htmlFor={`nota-${kind}-${id}`}>What was checked or done</label>
        <textarea id={`nota-${kind}-${id}`} value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
          className="w-full rounded-md border border-slate-200 dark:border-slate-700 dark:bg-slate-800 p-2 text-sm" />
        <Button size="sm" onClick={resolver} disabled={enviando || !nota.trim()} data-testid={`recon-resolve-${kind}-${id}`}>
          Mark resolved
        </Button>
        {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
      </div>
    </div>
  );
}

export default function AdminReconciliationPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setData(await fetchJson('/admin/reconciliation', { token }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [token]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="space-y-6" data-testid="admin-reconciliation-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Scale className="w-6 h-6 text-slate-500" />
          <div>
            <h1 className="text-2xl font-semibold">Reconciliation</h1>
            <p className="text-sm text-slate-500">Money left halfway that needs a person. Nothing here resolves by itself.</p>
          </div>
        </div>
        <Button variant="outline" onClick={cargar} disabled={cargando} data-testid="recon-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${cargando ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && <p className="text-sm text-red-600" role="alert" data-testid="recon-error">{error}</p>}
      {data && data.total === 0 && (
        <p className="text-sm text-emerald-700" data-testid="recon-empty">Nothing pending.</p>
      )}

      {data && SECCIONES.map(({ kind, title, help }) => {
        const items = data.sections[kind] || [];
        if (!items.length) return null;
        return (
          <section key={kind} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid={`recon-section-${kind}`}>
            <h2 className="font-semibold">{title} <span className="text-slate-400 font-normal">({items.length})</span></h2>
            <p className="text-xs text-slate-500 mb-2">{help}</p>
            {items.map((item) => (
              <Fila key={kind === 'rate_option' ? item.rate_id : item.id} kind={kind} item={item} token={token} onResolved={cargar} />
            ))}
          </section>
        );
      })}
    </div>
  );
}
